import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import {
    ConnectorTypeDefinition,
    ConnectorTypeSummary,
    ConnectorValidationResponse
} from "../../../models/bridge/connector-type.model";

/**
 * Service for fetching Connector Type Definitions from the API
 * These definitions drive the dynamic form rendering for connections
 */
@Injectable({
    providedIn: 'root'
})
export class BridgeConnectorTypeService {
    private readonly baseUrl: string;

    constructor(private httpClient: HttpClient) {
        this.baseUrl = `${environment.api.API_ENDPOINT}/4bridge/connector-types`;
    }

    /**
     * Get all active connector types (summary)
     */
    public getAll(): Observable<ConnectorTypeSummary[]> {
        return this.httpClient
            .get<ConnectorTypeSummary[]>(this.baseUrl)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Get full connector type definition by type
     * Includes all field definitions for form rendering
     */
    public getByType(connectorType: string): Observable<ConnectorTypeDefinition> {
        return this.httpClient
            .get<ConnectorTypeDefinition>(`${this.baseUrl}/${connectorType}`)
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Validate connection data against connector type definition
     */
    public validateConnectionData(
        connectorType: string,
        config: Record<string, any>,
        secrets: Record<string, string>
    ): Observable<ConnectorValidationResponse> {
        return this.httpClient
            .post<ConnectorValidationResponse>(`${this.baseUrl}/${connectorType}/validate`, {
                config,
                secrets
            })
            .pipe(
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
    }

    /**
     * Error handler
     */
    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'An error occurred';

        if (error.error instanceof ErrorEvent) {
            errorMessage = error.error.message;
        } else {
            errorMessage = error.error?.message || error.message || 'Server error';
        }

        console.error('Connector Type API Error:', errorMessage, error);
        return throwError(() => ({ message: errorMessage, status: error.status, error: error.error }));
    }
}
